import './maturity-compliance-rules.scss'

import { Button, Card, Select, Table, Typography } from 'antd'
import classnames from 'classnames'
import React, { useState } from 'react'
import { Column, Row } from 'react-display-flex'
import useSWR from 'swr'

import { fetchDetailByDomain } from '../../../../api'
import { momentDateHandler } from '../../../../date/moment-date-handler'
import { CreateMaturityComplianceRuleDrawer } from './CreateMaturityComplianceRule'
import { DeleteMaturityComplianceRuleModal } from './DeleteMaturityComplianceRule'
import { buildMaturityComplianceRulesTableColumns } from './maturity-compliance-rules-table-columns'
import { MaturityComplianceRule } from './models'
import { UpdateMaturityComplianceDrawer } from './UpdateMaturityComplianceRule'

const { formatDate, newDate } = momentDateHandler

interface MaturityComplianceRuleResponse {
  list: MaturityComplianceRule[]
}

export const MaturityComplianceRules = ({ tenant }) => {
  const [currentGroupId, setCurrentGroupId] = useState(null)
  const [ruleToUpdate, setRuleToUpdate] = useState(null)
  const [ruleToDelete, setRuleToDelete] = useState(null)
  const [isCreatingRule, setIsCreatingRule] = useState(false)

  const { data: maturityCompliancesGroups } = useSWR(
    tenant ? ['/compliances/maturities/groups', tenant] : null,
    fetchDetailByDomain,
  )

  const { data: maturityCompliances, mutate } = useSWR<MaturityComplianceRuleResponse>(
    currentGroupId ? [`/compliances/maturities/groups/${currentGroupId}/rules`, tenant] : null,
    fetchDetailByDomain,
  )

  const groupOptions = maturityCompliancesGroups
    ? maturityCompliancesGroups.list.map(({ id, startDate }) => ({
        label: `Group ${id} starting at ${formatDate({ date: newDate(startDate) })}`,
        value: id,
      }))
    : []

  const onDelete = (rule: MaturityComplianceRule) => setRuleToDelete(rule)

  const onUpdate = (rule: MaturityComplianceRule) => setRuleToUpdate(rule)

  const onCloseDialogs = () => {
    setRuleToDelete(null)
    setRuleToUpdate(null)
    setIsCreatingRule(false)
  }

  const onSuccess = () => {
    onCloseDialogs()
    mutate()
  }

  const columns = buildMaturityComplianceRulesTableColumns({ onDelete, onUpdate })

  return (
    <Column className="maturity-compliance-rules">
      <Card loading={!maturityCompliancesGroups}>
        <Select options={groupOptions} placeholder="Select a maturity compliance group" onSelect={setCurrentGroupId} />
      </Card>

      <Card
        title={<ComplianceTitle currentGroupId={currentGroupId} onCreate={setIsCreatingRule} />}
        loading={!maturityCompliancesGroups}
      >
        {!maturityCompliances ? (
          <p>Please select a maturity compliance group above</p>
        ) : (
          <Table
            rowClassName={(rule: MaturityComplianceRule) =>
              classnames({ active: rule.id === (ruleToUpdate?.id || ruleToDelete?.id) })
            }
            dataSource={maturityCompliances?.list}
            loading={!maturityCompliances}
            columns={columns}
            pagination={false}
            rowKey="id"
            size="small"
          />
        )}
      </Card>

      <CreateMaturityComplianceRuleDrawer
        domain={tenant}
        groupId={currentGroupId}
        isVisible={isCreatingRule}
        onClose={onCloseDialogs}
        onSuccess={onSuccess}
      />

      <UpdateMaturityComplianceDrawer
        domain={tenant}
        groupId={currentGroupId}
        maturityComplianceRule={ruleToUpdate}
        isVisible={currentGroupId && ruleToUpdate?.id}
        onClose={onCloseDialogs}
        onSuccess={onSuccess}
      />

      <DeleteMaturityComplianceRuleModal
        domain={tenant}
        groupId={currentGroupId}
        rule={ruleToDelete}
        isVisible={currentGroupId && ruleToDelete?.id}
        onClose={onCloseDialogs}
        onSuccess={onSuccess}
      />
    </Column>
  )
}

const ComplianceTitle = ({ currentGroupId, onCreate }) => (
  <Row alignContentCenter justifyContentSpaceBetween>
    <Typography.Title level={3}>{`Maturity Compliance ${
      currentGroupId ? 'for Group ' + currentGroupId : ''
    } `}</Typography.Title>
    {currentGroupId && (
      <Button type="primary" onClick={onCreate}>
        Create Rule
      </Button>
    )}
  </Row>
)
